/**
 * Pure helpers for a `type: 'package'` line on an estimate / order.
 *
 * A package line = a package + one of its variants (price / serves / dishes) plus the line's own editable
 * snapshot of the dishes. The package id lives in `packageId` on every FE model and is only turned into the
 * wire `itemId` in the sagas. The line's serves text lives in `packageServes` (wire: `servesCount`) so it
 * never collides with the numeric tray `servesCount` used by catalogue lines.
 */
import type { LinePackageItemBody } from '../../../services/cateringApi';
import type {
  CateringFormLineItem,
  CateringPackage,
  PackageItemRef,
  PackageVariant,
} from '../data/types';
import { packagePickerState } from './packageAvailability';
import { toPackageItemInputs } from './packageItems';
import { packageServesError, findVariant, getDefaultVariant, hasMultipleVariants, sortPackages, variantOptionLabel, variantTitle } from './packageVariants';
import { round2 } from './validationHelpers';

/** BE sends `type: 'PACKAGE'` (or null); the FE uses lowercase `'package'`. */
export function isPackageApiLine(type: string | null | undefined): boolean {
  return (type ?? '').toUpperCase() === 'PACKAGE';
}

/**
 * Package dishes as sent on a line — same shape and rules as a variant's own list: known items go back
 * "as received", new custom dishes are `{ name, sortOrder }`; deduped, capped, in display order, `sortOrder`
 * = index on every item. `[]` stays `[]` (never omitted: BE would re-copy the variant's list and drop the
 * line's edits).
 */
export function toLinePackageItems(items: readonly PackageItemRef[] | null | undefined): LinePackageItemBody[] {
  return toPackageItemInputs(items ?? []);
}

// ── What can be offered ─────────────────────────────────────────────────────────

/** One entry of the line-item package picker: a package + one of its variants. */
export interface PackageVariantOption {
  /** `${packageId}:${variantId}` — unique, used as the select value. */
  key: string;
  pkg: CateringPackage;
  variant: PackageVariant;
  /** "Package (Serves 30-40)" / "Package (Large)"; just the package name when it has one serving option. */
  label: string;
  /** The heading this option is listed under: set for a package with several serving options, `null` for a single-option package (a plain row). */
  group: { id: string; label: string } | null;
  /**
   * `YYYY-MM-DD` when orders for this package have not opened yet but the event is inside its availability window: the
   * picker shows the option greyed out ("Opens Dec 10") and it cannot be picked. `null` for an option that can be picked.
   */
  opensOn: string | null;
}

export const variantOptionKey = (packageId: string, variantId: string) => `${packageId}:${variantId}`;

/**
 * Every variant of every package the picker lists, packages in list order, variants in their own order:
 *  - only packages whose availability window covers the EVENT date (`packagePickerState`); the order-taking date alone
 *    never lists a package;
 *  - of those, one whose orders have NOT OPENED yet (today before its order-starting date) is listed with `opensOn` set,
 *    to be shown greyed out ("Order starts from Dec 10") and not selectable.
 * (Variants have a price by definition, so there is no "unpriced package" filter.)
 * A saved line whose package has dropped out of this list keeps working under its "(no longer available)" entry.
 */
export function packageVariantOptions(
  packages: readonly CateringPackage[],
  today?: string | null,
  eventDate?: string | null,
): PackageVariantOption[] {
  return sortPackages(packages).flatMap((pkg) => {
    const { listed, opensOn } = packagePickerState(pkg, today, eventDate);
    if (!listed) return [];
    // Figma picker: a package with several serving options is a heading over "Package (Serves N)" rows;
    // one with a single option is a plain row with just its name.
    const multiple = hasMultipleVariants(pkg);
    return pkg.variants.map((variant) => ({
      key: variantOptionKey(pkg.id, variant.id),
      pkg,
      variant,
      label: multiple ? variantTitle(pkg.name, variant) : pkg.name,
      group: multiple ? { id: pkg.id, label: pkg.name } : null,
      opensOn,
    }));
  });
}

// ── One line per package option ─────────────────────────────────────────────────

type PackageOptionRow = Pick<CateringFormLineItem, 'type' | 'packageId' | 'packageVariantId'>;

/** The package option (package + variant) a line sells, or `null` when it is not a package line / has none yet. */
export function packageRowOptionKey(row: PackageOptionRow): string | null {
  return row.type === 'package' && row.packageId && row.packageVariantId ? variantOptionKey(row.packageId, row.packageVariantId) : null;
}

/**
 * Package options already on a line OTHER than the one at exceptIndex. A package option can be on an estimate / order only once
 * (to sell more of it, raise the quantity); another variant of the same package is a different option and is fine.
 */
export function takenPackageOptionKeys(rows: readonly PackageOptionRow[], exceptIndex = -1): Set<string> {
  const taken = new Set<string>();
  rows.forEach((row, index) => {
    if (index === exceptIndex) return;
    const key = packageRowOptionKey(row);
    if (key) taken.add(key);
  });
  return taken;
}

/**
 * Row id → message for every package line that repeats the option of an EARLIER line (the first one is the real one).
 * The picker no longer offers a taken option, so this only fires for a duplicate that arrived some other way — an older
 * saved estimate / order, or a copy of one.
 */
export function duplicatePackageOptionIssues(
  rows: readonly (PackageOptionRow & { id?: string; name?: string; packageVariantName?: string | null })[],
  /** The picker's own label for an option key, so the message reads like the list ("Package (Serves 5)"). */
  labelOf?: (optionKey: string) => string | undefined,
): Record<string, string> {
  const seen = new Set<string>();
  const issues: Record<string, string> = {};
  rows.forEach((row, index) => {
    const key = packageRowOptionKey(row);
    if (!key) return;
    if (seen.has(key)) {
      const label = labelOf?.(key) ?? variantOptionLabel(row.name || 'Package', { name: row.packageVariantName ?? null, serves: '' });
      issues[row.id ?? `#${index}`] = `${label} is already on another line. Change that line's quantity instead, or choose a different serving option.`;
    }
    seen.add(key);
  });
  return issues;
}

// ── Quantity ────────────────────────────────────────────────────────────────────

export interface QtyBounds {
  min: number | null;
  max: number | null;
}

export function packageQtyBounds(pkg: CateringPackage | undefined): QtyBounds {
  return { min: pkg?.minQtyRequired ?? null, max: pkg?.maxQtyRequired ?? null };
}

/** Starts at 1 (never follows headcount), or the package's minimum when that is higher. */
export function initialPackageQty(pkg: CateringPackage): number {
  const { min, max } = packageQtyBounds(pkg);
  const start = Math.max(1, min ?? 1);
  return max !== null ? Math.min(start, max) : start;
}

export function packageQtyError(pkg: CateringPackage | undefined, qty: number): string | undefined {
  const { min, max } = packageQtyBounds(pkg);
  if (min !== null && qty < min) return `Minimum quantity for this package is ${min}`;
  if (max !== null && qty > max) return `Maximum quantity for this package is ${max}`;
  return undefined;
}

// ── Building / rebuilding a form row ────────────────────────────────────────────

/**
 * Row fields for picking `pkg` on `variant` (default variant when omitted). Everything else on the row is
 * left to the caller. Unit price = the variant price; serves = the variant's serves text (editable).
 */
export function buildPackageRow(
  pkg: CateringPackage,
  variant: PackageVariant | undefined = getDefaultVariant(pkg),
): Partial<CateringFormLineItem> {
  const quantity = initialPackageQty(pkg);
  const unitPrice = round2(variant?.price ?? 0);
  return {
    type: 'package',
    packageId: pkg.id,
    packageVariantId: variant?.id ?? null,
    packageVariantName: variant ? (variant.name?.trim() || variant.serves.trim() || null) : null,
    packageServes: variant?.serves ?? '',
    name: pkg.name,
    itemId: undefined,
    tierId: undefined,
    selectedTierIndex: undefined,
    quantity,
    unitPrice,
    lineTotal: round2(quantity * unitPrice),
    // The line starts as a copy of the variant's dishes; edits never touch the package.
    packageItems: (variant?.packageItems ?? []).map((i) => ({ ...i })),
    // A package is not a catalogue item: it uses the location's default tax (unless Tax Exempt).
    isDefaultItemTax: undefined,
    itemTaxes: undefined,
    pricingType: undefined,
    servesCount: undefined,
  };
}

/** The package-specific fields to spread when rebuilding a form row from a saved estimate/order line. */
export function packageRowFields(line: {
  packageId?: string;
  packageVariantId?: string | null;
  packageVariantName?: string | null;
  packageTagIds?: string[];
  packageServes?: string | null;
  packageItems?: readonly PackageItemRef[] | null;
}): Pick<
  CateringFormLineItem,
  'type' | 'packageId' | 'packageVariantId' | 'packageVariantName' | 'packageTagIds' | 'packageServes' | 'packageItems'
> {
  return {
    type: 'package',
    packageId: line.packageId,
    packageVariantId: line.packageVariantId ?? null,
    packageVariantName: line.packageVariantName ?? null,
    packageTagIds: line.packageTagIds ?? [],
    packageServes: line.packageServes ?? null,
    packageItems: (line.packageItems ?? []).map((i) => ({ ...i })),
  };
}

/**
 * The live variant behind a saved row, or `undefined` when it has since been removed (the row keeps what
 * it stored; re-saving a removed variant is allowed by the BE).
 */
export function variantForRow(
  pkg: CateringPackage | undefined,
  row: Pick<CateringFormLineItem, 'packageVariantId'>,
): PackageVariant | undefined {
  return findVariant(pkg, row.packageVariantId);
}

// ── Validation (used by both the estimate and the order form) ───────────────────

export interface PackageRowErrors {
  item?: string;
  qty?: string;
  price?: string;
  serves?: string;
}

/** `pkg` is the live package for `row.packageId`, or undefined when it has been deleted (then no bounds apply). */
export function validatePackageRow(row: CateringFormLineItem, pkg: CateringPackage | undefined): PackageRowErrors {
  const errors: PackageRowErrors = {};
  if (!row.packageId) {
    errors.item = 'Select a package';
    return errors;
  }
  const qtyError = packageQtyError(pkg, row.quantity);
  if (qtyError) errors.qty = qtyError;
  if (!Number.isFinite(row.unitPrice) || row.unitPrice < 0) errors.price = 'Enter a valid price';
  const servesError = packageServesError(row.packageServes);
  if (servesError) errors.serves = servesError;
  return errors;
}
